// Reference Explore-stats page (canonical pattern for the build loop).
// Recreates the Handbook "Sitting pattern statistics" page:
// stat cards, sitting days/year by chamber, day-of-week, dates-of-year heatmap.
// A year-range slider narrows every chart + table below to a window of years
// (e.g. only recent Parliaments) so the dates-of-year heatmap can inform
// forward planning from recent sitting patterns.

import React, { useState, useEffect, useCallback } from 'react';
import { Box, Paper, Grid, Typography, LinearProgress } from '@mui/material';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';
import { StatLine, StatBar } from './charts';
import YearRangeSlider from '../YearRangeSlider';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const DEFAULT_SPAN_YEARS = 10;

function StatCards({ cards }) {
  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {cards.map((c) => (
        <Grid item xs={6} md={4} key={c.label}>
          <Paper elevation={1} sx={{ p: 2, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h3" color="primary">{c.value}</Typography>
            <Typography variant="body2" color="text.secondary">{c.label}</Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
}

function CalendarHeatmap({ points, range }) {
  const max = points.reduce((m, p) => Math.max(m, p.count), 0) || 1;
  const lookup = {};
  points.forEach((p) => { lookup[`${p.month}-${p.day}`] = p.count; });
  const yearLabel = range
    ? (range[0] === range[1] ? `${range[0]}` : `${range[0]}–${range[1]}`)
    : '';
  return (
    <Box sx={{ overflowX: 'auto' }}>
      {MONTHS.map((mName, mi) => (
        <Box key={mName} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Box sx={{ width: 36, fontSize: 12, color: 'text.secondary' }}>{mName}</Box>
          {Array.from({ length: 31 }, (_, di) => {
            const count = lookup[`${mi + 1}-${di + 1}`] || 0;
            const intensity = count ? 0.15 + 0.85 * (count / max) : 0;
            return (
              <Box key={di}
                   title={count
                     ? `${mName} ${di + 1} · ${count} sitting day${count === 1 ? '' : 's'} (all chambers)${yearLabel ? `, ${yearLabel}` : ''}`
                     : ''}
                   sx={{
                     width: 14, height: 14, mr: '2px', borderRadius: '2px',
                     backgroundColor: count
                       ? `rgba(46,125,50,${intensity})` : '#f0f0f0',
                   }} />
            );
          })}
        </Box>
      ))}
    </Box>
  );
}

export default function SittingPatternStats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);      // initial load only
  const [refetching, setRefetching] = useState(false); // range-change refetch
  const [error, setError] = useState(null);
  const [bounds, setBounds] = useState(null);        // { min, max } full corpus
  const [range, setRange] = useState(null);          // [from, to] slider value

  const fetchData = useCallback(async (params, isInitial) => {
    try {
      if (!isInitial) setRefetching(true);
      const res = await getExploreStat('sitting-patterns', params);
      setData(res);
      if (isInitial && res.year_min != null && res.year_max != null) {
        setBounds({ min: res.year_min, max: res.year_max });
        // Default the slider to the last DEFAULT_SPAN_YEARS calendar years,
        // clamped into the corpus. year_from is sent open-ended (no year_to),
        // so the fetched window is exactly [defaultFrom, year_max].
        const defaultFrom = Math.max(
          res.year_min,
          Math.min(new Date().getFullYear() - (DEFAULT_SPAN_YEARS - 1),
                   res.year_max));
        setRange([defaultFrom, res.year_max]);
      }
      setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load statistics');
    } finally {
      if (isInitial) setLoading(false);
      setRefetching(false);
    }
  }, []);

  // First load applies the default window (last 10 years) so the heatmap and
  // charts start focused on recent sittings; widen the slider for full history.
  useEffect(() => {
    const defaultFrom = new Date().getFullYear() - (DEFAULT_SPAN_YEARS - 1);
    fetchData({ year_from: defaultFrom }, true);
  }, [fetchData]);

  // Refetch only when the committed range is narrower than the full corpus;
  // omitting a bound tells the backend "no limit" on that side.
  const handleCommit = useCallback((newRange) => {
    if (!bounds) return;
    const params = {};
    if (newRange[0] > bounds.min) params.year_from = newRange[0];
    if (newRange[1] < bounds.max) params.year_to = newRange[1];
    fetchData(params, false);
  }, [bounds, fetchData]);

  const charts = data?.charts || {};

  return (
    <ExploreStatsPage
      title="Sitting pattern statistics"
      description="The number of sitting days per year, the highest, lowest and average per chamber, the typical days of the week sittings fall on, and a heat map of when in the year sittings occur."
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName="sitting-pattern-statistics"
    >
      {data && (
        <>
          {bounds && range && (
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Year range</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Narrow every chart and table below to a range of years. Focusing
                on recent Parliaments makes the dates-of-year heatmap a better
                basis for forward-planning the coming year&apos;s sitting pattern.
              </Typography>
              <YearRangeSlider
                label="Years"
                value={range}
                onChange={setRange}
                onChangeCommitted={handleCommit}
                min={bounds.min}
                max={bounds.max}
                disabled={refetching}
              />
              {refetching && <LinearProgress sx={{ mt: 2 }} />}
            </Paper>
          )}

          <Box sx={{ opacity: refetching ? 0.5 : 1, transition: 'opacity 0.2s' }}>
            <StatCards cards={charts.stat_cards || []} />

            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Number of sitting days per year</Typography>
              <StatLine data={charts.per_year || []} xKey="year"
                        keys={charts.series || []} />
            </Paper>

            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Days of the week</Typography>
              <StatBar data={charts.day_of_week || []} xKey="day" yKey="count" />
            </Paper>

            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Dates of the year</Typography>
              <CalendarHeatmap points={charts.calendar_heatmap || []} range={range} />
            </Paper>
          </Box>
        </>
      )}
    </ExploreStatsPage>
  );
}
